/**
 * Zugriff auf Seitenlayout, Abschnitte, Kopf-/Fußzeilen und Felder.
 *
 * Die Verfügbarkeit ist hier uneinheitlich: Abschnitte sowie Kopf- und
 * Fußzeilen gibt es seit WordApi 1.1, Felder erst ab 1.4 und die Seiteneinrichtung
 * nur in der Desktop-Version. Jede Funktion sagt über `null` deutlich, wenn sie
 * in dieser Word-Version nichts liefern kann.
 */

import { supportsWordApi, supportsWordApiDesktop, wordApi, wordApiDesktop } from "./capabilities";
import { isWordAvailable, WordUnavailableError } from "./document";

export interface SectionInfo {
  index: number;
  /** Text der Kopfzeile des Abschnitts (Typ "Primary"). */
  headerText: string;
  /** Text der Fußzeile des Abschnitts (Typ "Primary"). */
  footerText: string;
}

export interface LayoutSnapshot {
  sections: SectionInfo[];
  /** Enthält mindestens eine Kopfzeile Text? */
  hasHeaderText: boolean;
  /** Enthält mindestens eine Fußzeile Text? */
  hasFooterText: boolean;
}

/** Liest Abschnitte samt Kopf- und Fußzeilen aus. */
export async function readLayout(): Promise<LayoutSnapshot> {
  if (!isWordAvailable()) throw new WordUnavailableError();

  return Word.run(async (context) => {
    const sections = context.document.sections;
    sections.load("items");
    await context.sync();

    const bodies = sections.items.map((section) => ({
      header: section.getHeader(Word.HeaderFooterType.primary),
      footer: section.getFooter(Word.HeaderFooterType.primary),
    }));
    for (const entry of bodies) {
      entry.header.load("text");
      entry.footer.load("text");
    }
    await context.sync();

    const infos: SectionInfo[] = bodies.map((entry, index) => ({
      index,
      headerText: (entry.header.text ?? "").trim(),
      footerText: (entry.footer.text ?? "").trim(),
    }));

    return {
      sections: infos,
      hasHeaderText: infos.some((section) => section.headerText.length > 0),
      hasFooterText: infos.some((section) => section.footerText.length > 0),
    };
  });
}

export interface FieldInfo {
  /** Word-Feldtyp, z. B. "Page", "NumPages" oder "Toc". */
  type: string;
  /** Feldfunktion, also der Code hinter dem Feld – bei Seitenzahlen "PAGE". */
  code: string;
}

/**
 * Liest alle Felder in Haupttext, Kopf- und Fußzeilen aus.
 * Gibt `null` zurück, wenn WordApi 1.4 nicht verfügbar ist.
 */
export async function readFields(): Promise<FieldInfo[] | null> {
  if (!isWordAvailable()) throw new WordUnavailableError();
  if (!supportsWordApi(wordApi.fields)) return null;

  try {
    return await Word.run(async (context) => {
      const sections = context.document.sections;
      sections.load("items");
      await context.sync();

      const collections = [context.document.body.fields];
      for (const section of sections.items) {
        collections.push(section.getHeader(Word.HeaderFooterType.primary).fields);
        collections.push(section.getFooter(Word.HeaderFooterType.primary).fields);
      }
      for (const collection of collections) {
        collection.load("items/type,items/code");
      }
      await context.sync();

      return collections.flatMap((collection) =>
        collection.items.map((field) => ({
          type: typeof field.type === "string" ? field.type : "Unknown",
          code: typeof field.code === "string" ? field.code.trim() : "",
        })),
      );
    });
  } catch {
    return null;
  }
}

/** Gibt es im Dokument ein Feld dieses Typs? */
export function hasField(fields: FieldInfo[] | null, type: string): boolean {
  if (!fields) return false;
  return fields.some((field) => field.type.toLowerCase() === type.toLowerCase());
}

export interface PageSetupInfo {
  /** Seitenränder in Punkt (72 pt = 1 Zoll = 2,54 cm). */
  topMargin: number | null;
  bottomMargin: number | null;
  leftMargin: number | null;
  rightMargin: number | null;
  /** "Portrait" oder "Landscape". */
  orientation: string | null;
}

/**
 * Liest die Seiteneinrichtung des ersten Abschnitts.
 * Gibt `null` zurück, wenn das Add-in nicht in Word für Windows oder Mac läuft.
 */
export async function readPageSetup(): Promise<PageSetupInfo | null> {
  if (!isWordAvailable()) throw new WordUnavailableError();
  if (!supportsWordApiDesktop(wordApiDesktop.pageSetup)) return null;

  try {
    return await Word.run(async (context) => {
      const pageSetup = context.document.pageSetup;
      pageSetup.load("topMargin,bottomMargin,leftMargin,rightMargin,orientation");
      await context.sync();

      const toNumber = (value: unknown) =>
        typeof value === "number" && Number.isFinite(value) ? value : null;

      return {
        topMargin: toNumber(pageSetup.topMargin),
        bottomMargin: toNumber(pageSetup.bottomMargin),
        leftMargin: toNumber(pageSetup.leftMargin),
        rightMargin: toNumber(pageSetup.rightMargin),
        orientation: typeof pageSetup.orientation === "string" ? pageSetup.orientation : null,
      };
    });
  } catch {
    return null;
  }
}

/** Rechnet Punkt in Zentimeter um – Word zeigt Ränder in cm an. */
export function pointsToCentimeters(points: number): number {
  return Math.round((points / 72) * 2.54 * 100) / 100;
}
